import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx' // <-- NUEVO: Importamos la librería para leer Excel

import { Search } from './../../../Common/Tables/Search'
import { InstructorBase } from './../../../Layouts/Instructor/V2/InstructorBase'
import { Details } from './../../../Common/Modals/Details'
import { Confirm } from './../../../Common/Modals/Confirm'
import { AccessCode } from './../../../Common/Modals/AccessCode'
import { Loading } from './../../../Common/Loading'

import { LogErrors } from './../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'
import { HELP_TYPE } from './../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'

// Importaciones de nuestra capa de negocios
import { GetInstructors, GetAccessCode, UploadInstructorsBulk, ResetPassword, AcceptPendingInstructor, RejectPendingInstructor, UnlinkActiveInstructor } from './../../../../utilities/BL/Leader/Instructors'
import { ROLES } from './../../../../utilities/EL/Roles'


const Instructors = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()

    const [isLoading, setIsLoading] = useState(true)
    const [instructorsList, setInstructorsList] = useState([])
    const instructorsRef = useRef([])

    const [searchText, setSearchText] = useState('')
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })

    const [showAccessCodeModal, setShowAccessCodeModal] = useState(false)
    const [currentAccessCode, setCurrentAccessCode] = useState(null)

    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [instructorDetails, setInstructorDetails] = useState(null)

    // Estados para la carga masiva y vista previa de Excel
    const [showBulkAddModal, setShowBulkAddModal] = useState(false)
    const [selectedFileForBulk, setSelectedFileForBulk] = useState(null)
    const [excelPreview, setExcelPreview] = useState([]) // Guarda los primeros 10 registros

    // Estado para saber si hay un archivo sobrevolando la zona
    const [isDragging, setIsDragging] = useState(false)

    // Funciones para el Drag & Drop
    const handleDragOver = (e) => {
        e.preventDefault() // Obligatorio para que el navegador permita soltar cosas
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragging(false)
        
        // Verificamos si soltaron archivos
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFile = e.dataTransfer.files[0]
            
            // Le pasamos el archivo a tu función existente simulando el evento (e)
            HandleFileChange({ target: { files: [droppedFile] } })
            
            // Limpiamos los datos del drag
            e.dataTransfer.clearData()
        }
    }

    const actions = {
        ACCEPT: "ACCEPT",
        REJECT: "REJECT",
        UNLINK: "UNLINK",
        RESET: "RESET",
        REASSIGN_AND_UNLINK: "REASSIGN_AND_UNLINK"
    }

    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [actionTypeOfModal, setActionTypeOfModal] = useState(null)
    const [instructorConfirmDetails, setInstructorConfirmDetails] = useState(null)
    const [selectedIdForReassignment, setSelectedIdForReassignment] = useState('')

    // <-- CAMBIO 1: Modificamos la carga inicial para obtener el código silenciosamente
    useEffect(() => {
        async function ValidateAndLoad() {
            try {
                const tmpSession = await ValidateSession()
                if (tmpSession.success) {
                    await LoadInstructors()
                    await LoadInitialAccessCode() // <-- Cargamos el código al arrancar
                } else {
                    navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
                }
            } catch (error) {
                await LogErrors('ValidateAndLoad - Instructors', error)
            } finally {
                setIsLoading(false)
            }
        }
        ValidateAndLoad()
    }, [navigate])

    const LoadInstructors = async () => {
        setIsLoading(true)
        const response = await GetInstructors()
        if (response.success) {
            setInstructorsList(response.data)
            instructorsRef.current = response.data
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: response.message || 'No fue posible cargar los instructores.' })
        }
        setIsLoading(false)
    }

    // <-- NUEVA FUNCIÓN: Obtiene el código sin interrumpir la experiencia
    const LoadInitialAccessCode = async () => {
        const response = await GetAccessCode()
        if (response.success) {
            setCurrentAccessCode(response.data)
        }
        // Si falla silenciosamente, no mostramos error aquí, lo manejaremos en el click como respaldo
    }

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            }
            return { key, direction: 'asc' }
        })
    }

    const processedInstructors = useMemo(() => {
        const filtered = instructorsList.filter((row) =>
            (row.instructorName ?? '').toLowerCase().includes(searchText.toLowerCase())
        )

        if (!sortConfig.key) return filtered

        return [...filtered].sort((a, b) => {
            const aVal = a[sortConfig.key]
            const bVal = b[sortConfig.key]

            if (typeof aVal === 'string') {
                return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
            }
            return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
        })
    }, [instructorsList, searchText, sortConfig])

    // <-- CAMBIO 2: Solo abrimos el modal si ya tenemos el código. 
    // Si por red falló al inicio, intentamos recuperarlo.
    const HandleShowAccessCode = async () => {
        if (currentAccessCode) {
            // Caso ideal: ya se cargó al entrar a la página
            setShowAccessCodeModal(true)
        } else {
            // Método de rescate por si la carga silenciosa falló
            setIsLoading(true)
            const response = await GetAccessCode()
            setIsLoading(false)
            
            if (response.success) {
                setCurrentAccessCode(response.data)
                setShowAccessCodeModal(true)
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo obtener el código de vinculación.' })
            }
        }
    }

    const HandleFileChange = (e) => {
        const file = e.target.files[0]
        setSelectedFileForBulk(file)
        
        if (file) {
            const reader = new FileReader()
            reader.onload = (evt) => {
                try {
                    const bstr = evt.target.result
                    const wb = XLSX.read(bstr, { type: 'binary' })
                    const wsname = wb.SheetNames[0]
                    const ws = wb.Sheets[wsname]
                    const data = XLSX.utils.sheet_to_json(ws)
                    
                    setExcelPreview(data.slice(0, 10))
                } catch (error) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'El formato del archivo Excel no es válido o está corrupto.' })
                    setExcelPreview([])
                }
            }
            reader.readAsBinaryString(file)
        } else {
            setExcelPreview([])
        }
    }

    const HandleSubmitBulkFile = async () => {
        if (!selectedFileForBulk) return

        setIsLoading(true)
        const response = await UploadInstructorsBulk(selectedFileForBulk)
        setIsLoading(false)

        if (response.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo procesado con éxito. Los instructores han sido invitados/agregados.' })
            setShowBulkAddModal(false)
            setSelectedFileForBulk(null)
            setExcelPreview([])
            await LoadInstructors()
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: response.message || 'Error al procesar el archivo masivo.' })
        }
    }

    const PrepareActionModal = (action, instructor) => {
        setInstructorConfirmDetails(instructor)
        if (action === actions.UNLINK && instructor.groups && instructor.groups.length > 0) {
            setActionTypeOfModal(actions.REASSIGN_AND_UNLINK)
            setSelectedIdForReassignment('')
        } else {
            setActionTypeOfModal(action)
        }
        setShowConfirmationModal(true)
    }

    const ExecuteAction = async () => {
        try {
            if (!instructorConfirmDetails) return
            const idInstructor = instructorConfirmDetails.idInstructor
            let response = null

            setIsLoading(true)
            setShowConfirmationModal(false)

            switch (actionTypeOfModal) {
                case actions.ACCEPT: response = await AcceptPendingInstructor(idInstructor); break
                case actions.REJECT: response = await RejectPendingInstructor(idInstructor); break
                case actions.UNLINK: response = await UnlinkActiveInstructor(idInstructor); break
                case actions.REASSIGN_AND_UNLINK: response = await UnlinkActiveInstructor(idInstructor, selectedIdForReassignment); break
                case actions.RESET: response = await ResetPassword(idInstructor); break
                default: break
            }

            setIsLoading(false)

            if (response && response.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Acción completada con éxito.` })
                await LoadInstructors()
            } else if (response) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response.message || `No fue posible realizar la acción.` })
            }
        } catch (error) {
            setIsLoading(false)
            await LogErrors('ExecuteAction - Instructors', error)
        }
    }

    const eligibleInstructorsForReassign = useMemo(() => {
        if (!instructorConfirmDetails) return []
        return instructorsList.filter(
            (inst) => inst.userStatus === 2 && inst.idInstructor !== instructorConfirmDetails.idInstructor
        )
    }, [instructorsList, instructorConfirmDetails])

    return isLoading ? (
        <Loading />
    ) : (
        <>
            <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.LEADER.INSTRUCTORS} page={WEBPAGES.LEADER.INSTRUCTORS} role={ROLES.LEADER}>
                <section className='container Instructor__Instructors'>
                    
                    <p className='Instructor__Title Instructor__Title--Center'>Mis instructores</p>
                    
                    <p className='Instructor__Subtitle Instructor__Subtitle--Center'>
                        Total: {instructorsList.length}
                    </p>

                    <div className="Instructor__Text--Center">
                        {/* <-- CAMBIO 3: Actualizamos el manejador de click */}
                        <button type='button' className='Btn__Ztudia--Link Instructor__AccessCodeButton' onClick={HandleShowAccessCode}>
                            Ver código Ztudia
                        </button>
                    </div>

                    {instructorsList.length === 0 ? (
                        <p className='Instructor__Text--Center Instructor__SpaceUp'>Actualmente no hay instructores asociados a este centro.</p>
                    ) : (
                        <>
                            <div className="Instructor__SpaceUp">
                                <Search onChange={(v) => setSearchText(v)} />
                                
                                <div className='Instructor__Instructors__TableActions mt-4'>
                                    <button className='Btn__Ztudia--PrimaryOutline' onClick={() => setShowBulkAddModal(true)}>
                                        <i className="bi bi-cloud-upload"></i> Agregar varios instructores
                                    </button>
                                </div>
                            </div>

                            <div className='container mt-4 Tables--Shadow Tables--Background'>
                                <section className='Tables__Container'>
                                    <table className='Tables__Table Instructor__Instructors__ContainerTable__Table'>
                                        <thead>
                                            <tr className='Tables__Table__Row--Header'>
                                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Instructors__ContainerTable__Table__TextColumn' onClick={() => handleSort('instructorName')}>
                                                    Nombre {sortConfig.key === 'instructorName' && <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>}
                                                </td>
                                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Instructors__ContainerTable__Table__TextColumn' onClick={() => handleSort('userStatus')}>
                                                    Estatus {sortConfig.key === 'userStatus' && <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>}
                                                </td>
                                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn'></td>
                                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn'></td>
                                                <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn'></td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {processedInstructors.map((row, index) => (
                                                <tr key={index} className='Tables__Table__Row'>
                                                    <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Instructors__ContainerTable__Table__TextColumn'>
                                                        {row.instructorName}
                                                        <br />
                                                        <small className="Instructor__Text--Muted">Grupos asignados: {row.groups ? row.groups.length : 0}</small>
                                                    </td>
                                                    <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Instructors__ContainerTable__Table__TextColumn'>
                                                        {row.userStatus === 2 ? (
                                                            <span className="Instructor__StatusBadge Instructor__StatusBadge--Active">
                                                                <i className="bi bi-check-circle-fill"></i> Activo
                                                            </span>
                                                        ) : (
                                                            <span className="Instructor__StatusBadge Instructor__StatusBadge--Pending">
                                                                <i className="bi bi-hourglass-split"></i> Pendiente
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn'>
                                                        <button 
                                                            onClick={() => { setInstructorDetails(row); setShowDetailsModal(true); }} 
                                                            className='Btn__Ztudia--PrimaryOutline'>
                                                            Ver grupos
                                                        </button>
                                                    </td>
                                                    <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn'>
                                                        {row.userStatus === 1 ? (
                                                            <div className="Instructor__Instructors__DualAction">
                                                                <button onClick={() => PrepareActionModal(actions.ACCEPT, row)} className='Btn__Ztudia--PrimaryOutline' title="Aceptar">
                                                                    Aceptar {/* <i className='bi bi-check-lg'></i> */}
                                                                </button>
                                                                <button onClick={() => PrepareActionModal(actions.REJECT, row)} className='Btn__Ztudia--DangerOutline' title="Rechazar">
                                                                    Rechazar {/* <i className='bi bi-x-lg'></i> */}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button onClick={() => PrepareActionModal(actions.UNLINK, row)} className='Btn__Ztudia--DangerOutline'>
                                                                <i className='bi bi-person-x'></i> Desvincular
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn'>
                                                        {row.userStatus === 2 && (
                                                            <button onClick={() => PrepareActionModal(actions.RESET, row)} className='Btn__Ztudia--PrimaryOutline'>
                                                                <i className="bi bi-arrow-repeat"></i> Contraseña
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            </div>
                        </>
                    )}

                    {showAccessCodeModal && currentAccessCode && (
                        <AccessCode accessCode={currentAccessCode} setShowModal={setShowAccessCodeModal} />
                    )}

                    {showBulkAddModal && (
                        <Details stepButtons={false} showCloseModalButtonInContent={false} extraClassName={'Instructor__Instructors__Details'} setShowModal={setShowBulkAddModal} setDetailsData={() => {}}>
                            <div className='Instructor__Instructors__Details__Container'>
                                <p className='Instructor__Instructors__Details__Title'>Agregar varios instructores</p>
                                <a href="/assets/templates/Instructores.xlsx" download className='Btn__Ztudia--Link d-block text-center'>
                                    <i className="bi bi-download"></i> Descargar plantilla de ejemplo
                                </a>

                                <p className='Instructor__Text mt-4'>Sube tu libro de Excel (.xlsx o .xls) con los datos de los instructores que deseas agregar.</p>

                                {/* <input 
                                    type='file' 
                                    accept=".xls, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                    className='Form__Input d-block mx-auto' 
                                    onChange={HandleFileChange} 
                                /> */}

                                {/* <div className='Instructor__DropzoneContainer'>
                                    <label htmlFor="file-upload" className='Instructor__Dropzone'>
                                        <i className="bi bi-cloud-arrow-up Instructor__Dropzone__Icon"></i>
                                        <p className="Instructor__Dropzone__Text">Haz clic aquí para buscar tu archivo</p>
                                        <p className="Instructor__Dropzone__Subtext">Formatos soportados: .xls, .xlsx</p>
                                        
                                        <input 
                                            id="file-upload"
                                            type='file' 
                                            accept=".xls, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                            className='Instructor__Dropzone__Input' 
                                            onChange={HandleFileChange} 
                                        />
                                    </label>
                                </div> */}

                                <div className='Instructor__DropzoneContainer'>
                                    <label 
                                        htmlFor="file-upload" 
                                        className={`Instructor__Dropzone ${isDragging ? 'Instructor__Dropzone--Dragging' : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <i className="bi bi-cloud-arrow-up Instructor__Dropzone__Icon"></i>
                                        <p className="Instructor__Dropzone__Text">Haz clic o arrastra tu archivo aquí</p>
                                        <p className="Instructor__Dropzone__Subtext">Formatos soportados: .xls, .xlsx</p>
                                        
                                        <input 
                                            id="file-upload"
                                            type='file' 
                                            accept=".xls, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                            className='Instructor__Dropzone__Input' 
                                            onChange={HandleFileChange} 
                                        />
                                    </label>
                                </div>
                                
                                {excelPreview.length > 0 && (
                                    <div className="Instructor__SpaceUp">
                                        <p className="Instructor__Text--Bold">Vista previa{excelPreview.length > 1 && ` (mostrando ${excelPreview.length} registros)`}:</p>
                                        <div className='Instructor__Instructors__Details__ContainerTable Instructor__SpaceDown'>
                                            <table className='Instructor__Instructors__Details__Table'>
                                                <thead>
                                                    <tr>
                                                        <td className='Instructor__Text--Bold'>Nombre</td>
                                                        <td className='Instructor__Text--Bold'>Apellidos</td>
                                                        <td className='Instructor__Text--Bold'>Usuario</td>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {excelPreview.map((row, idx) => (
                                                        <tr key={idx} className='Instructor__Instructors__Details__Table__Row'>
                                                            <td className='Instructor__Instructors__Details__Table__Row__Cell'>{row.Nombre || row.nombre || 'N/A'}</td>
                                                            <td className='Instructor__Instructors__Details__Table__Row__Cell'>{row.Apellidos || row.apellidos || 'N/A'}</td>
                                                            <td className='Instructor__Instructors__Details__Table__Row__Cell'>{row.Usuario || row.usuario || 'N/A'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <div className='Instructor__SpaceUp' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                                    <button type='button' className='Btn__Ztudia--Primary' disabled={!selectedFileForBulk} onClick={HandleSubmitBulkFile}>
                                        Subir archivo ahora
                                    </button>
                                    <button type='button' className='Btn__Ztudia--DangerOutline' onClick={() => { setShowBulkAddModal(false); setSelectedFileForBulk(null); setExcelPreview([]); }}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </Details>
                    )}

                    {showDetailsModal && instructorDetails && (
                        <Details stepButtons={false} showCloseModalButtonInContent={true} extraClassName={'Instructor__Instructors__Details'} setShowModal={setShowDetailsModal} setDetailsData={setInstructorDetails}>
                            <div className='Instructor__Instructors__Details__Container'>
                                <p className='Instructor__Instructors__Details__Title'>Grupos de {instructorDetails.instructorName}</p>
                                <p className='text-center'>Total: {instructorDetails.groups.length}</p>
                                
                                {instructorDetails.groups && instructorDetails.groups.length > 0 ? (
                                    <div className='Instructor__Instructors__Details__ContainerTable'>
                                        <table className='Instructor__Instructors__Details__Table'>
                                            <thead>
                                                <tr>
                                                    <td className='Instructor__Text--Bold fw-bold'>Nombre del grupo</td>
                                                    <td className='Instructor__Text--Bold fw-bold'>Código Ztudia</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {instructorDetails.groups.map((grp, idx) => (
                                                    <tr key={idx} className='Instructor__Instructors__Details__Table__Row'>
                                                        <td className='Instructor__Instructors__Details__Table__Row__Cell'>{grp.groupName}</td>
                                                        <td className='Instructor__Instructors__Details__Table__Row__Cell'>{grp.groupAccessCode}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className='Instructor__Text--Center Instructor__SpaceUp'>Este instructor no tiene grupos asignados actualmente.</p>
                                )}
                            </div>
                        </Details>
                    )}

                    {showConfirmationModal && instructorConfirmDetails && (
                        <>
                            {(actionTypeOfModal === actions.ACCEPT || actionTypeOfModal === actions.REJECT || actionTypeOfModal === actions.RESET || actionTypeOfModal === actions.UNLINK) && (
                                <Confirm
                                    message={
                                        actionTypeOfModal === actions.ACCEPT ? `¿Estás seguro de aceptar a ${instructorConfirmDetails.instructorName}?` :
                                        actionTypeOfModal === actions.REJECT ? `¿Estás seguro de rechazar la solicitud de ${instructorConfirmDetails.instructorName}?` :
                                        actionTypeOfModal === actions.RESET  ? `¿Estás seguro de resetear la contraseña de ${instructorConfirmDetails.instructorName}?` :
                                        `¿Estás seguro de desvincular al instructor ${instructorConfirmDetails.instructorName} del centro?`
                                    }
                                    actionToYesButton={ExecuteAction}
                                    textYesButton={'Sí, confirmar'}
                                    textNoButton={'Cancelar'}
                                    setShowModal={setShowConfirmationModal}
                                    setConfirmData={setInstructorConfirmDetails}
                                />
                            )}

                            {actionTypeOfModal === actions.REASSIGN_AND_UNLINK && (
                                <Details stepButtons={false} showCloseModalButtonInContent={false} extraClassName={'Modal__Layout--Summary'} setShowModal={setShowConfirmationModal} setDetailsData={() => {}}>
                                    <div className='Modal__Layout--Summary__Container'>
                                        <p className='Modal__Layout--Summary__Title Instructor__Text--Danger'>¡Atención! Instructor con grupos activos</p>
                                        
                                        <p className='Instructor__Text Instructor__SpaceUp'>
                                            El instructor <strong>{instructorConfirmDetails.instructorName}</strong> tiene <strong>{instructorConfirmDetails.groups.length} grupo(s)</strong> a su cargo. 
                                            Para desvincularlo, debes transferir estos grupos a otro instructor activo.
                                        </p>

                                        <div className='Instructor__SpaceUp'>
                                            <label className='Form__Label'>Selecciona al nuevo instructor responsable:</label>
                                            <select 
                                                className='Form__Input' 
                                                value={selectedIdForReassignment} 
                                                onChange={(e) => setSelectedIdForReassignment(e.target.value)}
                                            >
                                                <option value="" disabled>-- Elige un instructor --</option>
                                                {eligibleInstructorsForReassign.map(inst => (
                                                    <option key={inst.idInstructor} value={inst.idInstructor}>
                                                        {inst.instructorName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {eligibleInstructorsForReassign.length === 0 && (
                                            <p className='Instructor__Message--Error Instructor__SpaceUp'>
                                                No hay otros instructores activos en el centro para recibir los grupos. No puedes desvincularlo hasta registrar otro.
                                            </p>
                                        )}

                                        <div className='Modal__Layout--Summary__Buttons Instructor__SpaceUp'>
                                            <button 
                                                type='button' 
                                                className='Btn__Ztudia--Danger' 
                                                disabled={!selectedIdForReassignment || eligibleInstructorsForReassign.length === 0} 
                                                onClick={ExecuteAction}
                                            >
                                                Transferir y Desvincular
                                            </button>
                                            <button type='button' className='Btn__Ztudia--PrimaryOutline' onClick={() => setShowConfirmationModal(false)}>
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                </Details>
                            )}
                        </>
                    )}

                </section>
            </InstructorBase>
        </>
    )
}

export { Instructors };